import { UserProfileViewContext } from "context/userProfile/UserProfileViewContext";
import { useAuthContext } from "hooks/useAuthContext";
import { useLocation, useNavigate } from "react-router";
import { ReactNode, useEffect, useState } from "react";
import { UserWorkspaceProfile } from "models/UserWorkspaceProfile";
import { useGetProfile } from "hooks/useGetProfile";
import { useGetUserProfilePhoto } from "hooks/useGetUserProfilePhoto";
import { RoutePaths } from "constants/RoutePaths";

interface UserProfileViewContextProviderProps {
  children: ReactNode;
}

export const UserProfileViewContextProvider: React.FC<UserProfileViewContextProviderProps> = ({ children }) => {
  const { userState, refreshMyProfile: fetchCurrentUserProfile } = useAuthContext();
  const [user, setUser] = useState<UserWorkspaceProfile | null>(null);
  const [isProfileOfLoggedUser, setIsProfileOfLoggedUser] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { getProfile, getProfilePending } = useGetProfile();
  const { getUserProfilePhotoUrl } = useGetUserProfilePhoto();

  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get('userId');

  const transformUserStateToProfile = (userState: any): UserWorkspaceProfile => ({
    displayName: userState.user.selectedWorkspace.userDisplayName,
    email: userState.user.email,
    id: userState.user.userId,
    role: userState.user.selectedWorkspace.userRole,
    workspace: {
      id: userState.user.selectedWorkspace.workspaceId,
      name: userState.user.selectedWorkspace.workspaceName
    },
    emailConfirmed: userState.user.emailConfirmed,
    hasLessons: userState.user.selectedWorkspace.hasUserLessons,
    hasSubordinates: userState.user.selectedWorkspace.hasUserSubordinates,
    profilePhotoUrl: userState.user.selectedWorkspace.userPhotoUrl,
    patrons: userState.user.selectedWorkspace.userSuperiors,
    tagA: userState.user.selectedWorkspace.userTagA,
    tagB: userState.user.selectedWorkspace.userTagB,
  });

  const fetchUserProfile = async (targetUserId: string) => {
    const getProfileResp = await getProfile(targetUserId);
    if (getProfileResp?.status === 200) {
      const profileData = getProfileResp.data;
      const profilePhotoUrl = await getUserProfilePhotoUrl(targetUserId);
      return profileData ? { ...profileData, profilePhotoUrl } : null;
    } else if (getProfileResp?.status === 403) {
      navigate(RoutePaths.UNAUTHORIZED);
      return null;
    }
    return null;
  };

  const refreshProfile = async () => {
    if (userId) {
      const profile = await fetchUserProfile(userId);
      setUser(profile);
      setIsProfileOfLoggedUser(false);
    } else {
      fetchCurrentUserProfile();
      setIsProfileOfLoggedUser(true);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (userId && userId !== userState.user.userId) {
        const profile = await fetchUserProfile(userId);
        setUser(profile);
        setIsProfileOfLoggedUser(false);
      } else if (userState.user.userId) {
        setUser(transformUserStateToProfile(userState));
        setIsProfileOfLoggedUser(true);
      }
    };

    fetchData();
  }, [userState.user, userId, getProfile]);

  return (
    <UserProfileViewContext.Provider value={{ isProfileOfLoggedUser, user, refreshProfile, getProfilePending }}>
      {children}
    </UserProfileViewContext.Provider>
  );
};